<script setup lang="ts">
  // Post-close surface: the orders of a closed table session, with the
  // mass-invoicing shortcut (shared flow in use-mass-invoice.ts). V2 shell
  // (replaces the legacy ModalTableSessionOrders): clickable rows drill into
  // the V2 order modal, which stacks on top while this one stays open.
  import { computed, getCurrentInstance, onMounted, ref } from 'vue';
  import { OrderPaymentStatus } from '@/enums/OrderEnums';
  import { IOrder } from '@/interfaces/IOrder';
  import { displayPrice, roundPrice } from '@/utils';
  import {
    isOrderFullyComped,
    isOrderInvoiceable,
    orderInvoiceableTotal,
  } from '@/utils/orders.utils';
  import { PermissionType } from '@/enums/PermissionType';
  import OrdersService from '@/api/services/orders.service';
  import { useAuth } from '@/composition/use-auth';
  import { useInvoiceAttempt } from '@/composition/use-invoice-attempt';
  import { useMassInvoice } from '@/composition/use-mass-invoice';
  import { useModal } from '@/composition/use-modal';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosV2OrderModal from '@/components/pos-v2/modals/PosV2OrderModal.vue';

  const props = defineProps<{
    tableSessionId: string;
  }>();

  const MODAL_NAME = 'pos-v2-session-orders';

  const modals = useModal();
  const restaurantStore = useRestaurantStore();
  const invoiceAttempt = useInvoiceAttempt();
  const auth = useAuth();
  const { invoiceLoading, promptAndInvoice } = useMassInvoice();

  // POST /orders/massInvoice requires OrdersManage server-side: an
  // OrdersHistoric-only user (who CAN open this modal from history) would
  // only get an error toast from the button, so hide it and its summary.
  // Single-order invoicing (inside the stacked order modal) stays available.
  const canManageOrders = computed(() =>
    auth.hasPermission(PermissionType.OrdersManage)
  );

  // vue-i18n in script (Vue 2.7): translate through the component proxy.
  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  //#region Data

  const orders = ref<IOrder[] | null>(null);
  const loadError = ref(false);

  async function loadOrders() {
    loadError.value = false;
    orders.value = null;
    try {
      orders.value = await OrdersService.getByTableSession(
        props.tableSessionId
      );
    } catch (e) {
      // Surfaced by the inline empty state only — a toast would duplicate it.
      console.error('Error loading table session orders', e);
      loadError.value = true;
      orders.value = [];
    }
  }

  onMounted(loadOrders);

  //#endregion

  //#region Rows / eligibility

  const title = computed(() => {
    const tableName = orders.value?.find((o) => o.table?.name)?.table?.name;
    return tableName
      ? i18nT('tableSession.modalTitleWithTable', { table: tableName })
      : i18nT('tableSession.modalTitle');
  });

  /**
   * Mass-invoice eligibility (mirrors PosV2TableFooter.invoiceableSelected):
   * Paid, not yet invoiced, admin-owned (one POS-owned order in the batch
   * makes POST /orders/massInvoice reject the whole request), something left
   * to invoice after the comps exclusion, and no live attempt.
   */
  const eligibleOrders = computed(() =>
    (orders.value ?? []).filter(
      (o) =>
        o.paymentStatus === OrderPaymentStatus.Paid &&
        !o.invoicingItemId &&
        o.posDeviceId == null &&
        !invoiceAttempt.isInvoiceLive(o.id) &&
        isOrderInvoiceable(o)
    )
  );

  const hasPosOwnedOrders = computed(() =>
    (orders.value ?? []).some((o) => o.posDeviceId != null)
  );

  // Same composition as the CreateInvoiceModalArg summary (Phase 1J).
  const summary = computed(() => {
    const eligible = eligibleOrders.value;
    if (!eligible.length) return null;

    const subtotal = roundPrice(
      eligible.reduce((acc, o) => acc + Number(o.total ?? 0), 0)
    );
    const invoiceTotal = roundPrice(
      eligible.reduce((acc, o) => acc + orderInvoiceableTotal(o), 0)
    );
    return {
      ordersCount: eligible.length,
      subtotal,
      excludedTotal: roundPrice(subtotal - invoiceTotal),
      invoiceTotal,
    };
  });

  function formatPrice(price: number) {
    const symbol = restaurantStore.currency?.symbol ?? '';
    return symbol + displayPrice(price, true);
  }

  /** dd/MM, HH:mm — compact secondary-line format. */
  function formatDateTime(date: string | Date): string {
    const d = new Date(date);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}, ${pad(
      d.getHours()
    )}:${pad(d.getMinutes())}`;
  }

  function orderTitle(order: IOrder) {
    return `#${order.humanId ?? order.id}`;
  }

  // Orders in one session can sit on different tables after mid-session
  // moves, so the per-order table name must not be dropped.
  function orderSubtitle(order: IOrder): string {
    const time = formatDateTime(order.createdAt);
    return order.table?.name ? `${order.table.name} · ${time}` : time;
  }

  /**
   * Formatted partial invoiceable amount, shown only when comps exclude a
   * portion of the total. Pre-formatted so the template needs no non-null
   * assertion.
   */
  function partialInvoiceableAmount(order: IOrder): string | null {
    const invoiceable = orderInvoiceableTotal(order);
    if (invoiceable > 0 && invoiceable < Number(order.total ?? 0)) {
      return formatPrice(invoiceable);
    }
    return null;
  }

  //#endregion

  function openOrder(order: IOrder) {
    // Stacks on top — this session modal stays open underneath.
    modals.showDynamicDialog(
      PosV2OrderModal,
      { orderId: order.id },
      'pos-v2-order-modal',
      { width: 1100 }
    );
  }

  async function invoiceEligible() {
    await promptAndInvoice(eligibleOrders.value);
    // promptAndInvoice already stamps invoicingItemId on the passed
    // instances; the reload is belt-and-braces so freshly-invoiced rows
    // re-render with their chip deterministically.
    void loadOrders();
  }

  function handleClose() {
    modals.hide(MODAL_NAME);
  }
</script>

<template>
  <div class="pos-v2-session-orders-modal">
    <div class="modal-header">
      <h2 class="modal-title">{{ title }}</h2>
      <PosUiButton
        icon="close"
        color="neutral"
        variant="ghost"
        size="md"
        @click="handleClose"
      />
    </div>

    <div class="modal-body">
      <!-- Loading -->
      <div v-if="!orders" class="loading-state">
        <span class="loading-spinner"></span>
      </div>

      <!-- Load error -->
      <PosUiEmptyState
        v-else-if="loadError"
        icon="alert-circle-outline"
        :title="i18nT('tableSession.loadError')"
        class="error-state"
      />

      <template v-else>
        <div class="orders-list">
          <button
            v-for="order in orders"
            :key="order.id"
            type="button"
            class="order-row"
            @click="openOrder(order)"
          >
            <div class="row-main">
              <span class="row-title">{{ orderTitle(order) }}</span>
              <span class="row-subtitle">{{ orderSubtitle(order) }}</span>
            </div>

            <div class="row-chips">
              <PosUiBadge
                v-if="order.invoicingItemId"
                color="success"
                variant="soft"
                size="xs"
              >
                {{ $t('tableSession.invoicedChip') }}
              </PosUiBadge>
              <PosUiBadge
                v-if="isOrderFullyComped(order)"
                color="info"
                variant="soft"
                size="xs"
              >
                {{ $t('tableSession.compedChip') }}
              </PosUiBadge>
              <PosUiBadge
                v-if="order.posDeviceId != null"
                color="neutral"
                variant="soft"
                size="xs"
              >
                {{ $t('tableSession.posChip') }}
              </PosUiBadge>
            </div>

            <div class="row-right">
              <div class="row-amounts">
                <span class="row-total">{{ formatPrice(order.total) }}</span>
                <span
                  v-if="partialInvoiceableAmount(order)"
                  class="row-invoiceable"
                >
                  {{ $t('tableSession.colInvoiceable') }}
                  {{ partialInvoiceableAmount(order) }}
                </span>
              </div>
              <PosUiIcon name="chevron-right" class="row-chevron" />
            </div>
          </button>
        </div>

        <div v-if="hasPosOwnedOrders" class="pos-note">
          <PosUiIcon name="information-outline" class="note-icon" />
          <span>{{ $t('tableSession.posOwnedNote') }}</span>
        </div>

        <div v-if="summary && canManageOrders" class="invoice-summary">
          <div class="invoice-summary-orders">
            {{
              $tc('invoice.summary.orders', summary.ordersCount, {
                count: summary.ordersCount,
              })
            }}
          </div>
          <div class="invoice-summary-line">
            <span>{{ $t('invoice.summary.subtotal') }}</span>
            <span>{{ formatPrice(summary.subtotal) }}</span>
          </div>
          <div
            v-if="summary.excludedTotal > 0"
            class="invoice-summary-line invoice-summary-line--excluded"
          >
            <span>{{ $t('invoice.summary.excluded') }}</span>
            <span>-{{ formatPrice(summary.excludedTotal) }}</span>
          </div>
          <div class="invoice-summary-line invoice-summary-line--total">
            <span>{{ $t('invoice.summary.total') }}</span>
            <span>{{ formatPrice(summary.invoiceTotal) }}</span>
          </div>
        </div>
      </template>
    </div>

    <div class="modal-footer">
      <PosUiButton
        color="neutral"
        variant="outline"
        size="lg"
        class="btn-close"
        @click="handleClose"
      >
        {{ $t('posV2.close') }}
      </PosUiButton>

      <PosUiButton
        v-if="canManageOrders"
        color="primary"
        variant="solid"
        size="lg"
        class="btn-invoice"
        :disabled="eligibleOrders.length === 0"
        :loading="invoiceLoading"
        @click="invoiceEligible"
      >
        {{ $t('posV2.invoice') }}
      </PosUiButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
  // Root lives inside .vm--modal.dialog--height-limit (a max-height flex
  // parent): the body scrolls and shrinks on short screens.
  .pos-v2-session-orders-modal {
    @apply tw-flex tw-max-h-full tw-min-h-0 tw-w-full tw-flex-col;

    .modal-header {
      @apply tw-flex tw-flex-none tw-items-center tw-justify-between tw-gap-3 tw-border-b tw-border-neutral-200 tw-px-4 tw-py-3;

      .modal-title {
        @apply tw-text-lg tw-font-bold tw-text-neutral-900;
      }
    }

    .modal-body {
      @apply tw-flex tw-min-h-0 tw-flex-col tw-gap-3 tw-overflow-y-auto tw-p-4;
      max-height: 70vh;

      .loading-state {
        @apply tw-flex tw-min-h-[160px] tw-items-center tw-justify-center;

        .loading-spinner {
          @apply tw-h-9 tw-w-9 tw-animate-spin tw-rounded-full tw-border-4 tw-border-orange-600 tw-border-t-transparent;
        }
      }

      .error-state {
        @apply tw-min-h-[160px];
      }

      .orders-list {
        @apply tw-flex tw-flex-none tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-white;

        .order-row {
          @apply tw-flex tw-min-h-[44px] tw-w-full tw-cursor-pointer tw-items-center tw-gap-3 tw-border-b tw-border-neutral-100 tw-px-3 tw-py-2 tw-text-left tw-transition-colors;

          &:last-child {
            @apply tw-border-b-0;
          }

          &:hover {
            @apply tw-bg-neutral-50;
          }

          .row-main {
            @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col;

            .row-title {
              @apply tw-text-sm tw-font-bold tw-text-neutral-900;
            }

            .row-subtitle {
              @apply tw-truncate tw-text-xs tw-text-neutral-500;
            }
          }

          .row-chips {
            @apply tw-flex tw-flex-none tw-flex-wrap tw-items-center tw-justify-end tw-gap-1;
          }

          .row-right {
            @apply tw-flex tw-flex-none tw-items-center tw-gap-1.5;

            .row-amounts {
              @apply tw-flex tw-flex-col tw-items-end;

              .row-total {
                @apply tw-text-sm tw-font-bold tw-tabular-nums tw-text-neutral-900;
              }

              .row-invoiceable {
                @apply tw-text-xs tw-tabular-nums tw-text-neutral-500;
              }
            }

            .row-chevron {
              @apply tw-text-lg tw-text-neutral-300;
            }
          }
        }
      }

      .pos-note {
        @apply tw-flex tw-flex-none tw-items-start tw-gap-2 tw-rounded-lg tw-bg-neutral-50 tw-px-3 tw-py-2 tw-text-sm tw-text-neutral-600;

        .note-icon {
          @apply tw-mt-0.5 tw-flex-none tw-text-base tw-text-neutral-400;
        }
      }

      .invoice-summary {
        @apply tw-flex-none tw-rounded-lg tw-border tw-border-neutral-200 tw-bg-neutral-50 tw-px-4 tw-py-3;

        .invoice-summary-orders {
          @apply tw-mb-2 tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-neutral-500;
        }

        .invoice-summary-line {
          @apply tw-flex tw-items-center tw-justify-between tw-text-sm tw-text-neutral-700;

          & + .invoice-summary-line {
            @apply tw-mt-1;
          }

          &--excluded {
            @apply tw-text-orange-700;
          }

          &--total {
            @apply tw-mt-2 tw-border-t tw-border-neutral-200 tw-pt-2 tw-font-semibold tw-text-neutral-900;
          }
        }
      }
    }

    .modal-footer {
      @apply tw-flex tw-flex-none tw-items-center tw-justify-end tw-gap-2 tw-border-t tw-border-neutral-200 tw-px-4 tw-py-3;

      :deep(.btn-close),
      :deep(.btn-invoice) {
        @apply tw-min-w-[150px];
      }
    }
  }
</style>
